import React, { useRef, useState } from 'react';
import { api } from '../api';
import { defaultSheet } from '../datasheet';
import DatasheetPreview from './DatasheetPreview';

const ACCEPT_RE = /\.(pdf|png|jpe?g|webp|docx|xlsx|xlsm|pptx|csv|txt|md)$/i;
const ACCEPT_ATTR = '.pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx,.xlsm,.pptx,.csv,.txt,.md';

export default function AiImportView({ toast, onOpenInEditor }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [result, setResult] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [names, setNames] = useState(null);
  const [namesBusy, setNamesBusy] = useState(false);
  const [queued, setQueued] = useState([]);
  const [stage, setStage] = useState('');
  const fileRef = useRef(null);

  async function suggestNames() {
    setNamesBusy(true);
    try {
      const r = await api.aiSuggestNames(result.sheet);
      setNames(r.names || []);
      if (!(r.names || []).length) toast('No name ideas came back — try again', true);
    } catch (e) {
      toast('Name suggestion failed: ' + ((e.response && e.response.data && e.response.data.detail) || e.message), true);
    }
    setNamesBusy(false);
  }

  function addFiles(list) {
    const incoming = Array.from(list || []).filter(f => ACCEPT_RE.test(f.name || ''));
    if (!incoming.length) { toast('Use a PDF, an image (PNG/JPG/WEBP) or a Word/Excel/PowerPoint file', true); return; }
    setQueued(q => [...q, ...incoming].slice(0, 6));
  }

  async function extract() {
    if (!queued.length) return;
    setBusy(true); setResult(null); setNames(null); setStage('');
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    const firstPdf = queued.find(f => /\.pdf$/i.test(f.name));
    setPdfUrl(firstPdf ? URL.createObjectURL(firstPdf) : '');
    try {
      const r = await api.aiExtractDatasheet(queued, setStage);
      const sheet = { ...defaultSheet(), ...(r.sheet || {}) };
      setResult({ sheet, warnings: r.warnings || [], filename: r.filename || '', model: r.model || '' });
      toast('Draft datasheet ready — review it against the source' + (r.model ? ' (read by ' + r.model + ')' : ''));
    } catch (e) {
      const msg = (e.response && e.response.data && e.response.data.detail) || e.message;
      toast('Extraction failed: ' + msg, true);
    }
    setBusy(false);
  }

  function discard() {
    setResult(null);
    setNames(null);
    setQueued([]);
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(''); }
  }

  return (
    <div className="ai-import-page" data-testid="ai-import-page">
      <h2>AI Import — supplier documents to datasheet</h2>
      <p className="codegen-sub">Drop in whatever the supplier sent — quote, spec sheet, photo of a sheet, Word or Excel. Add <b>several files at once</b> and they're merged into one draft: specs from the spec sheet, model codes from the quote. Nothing is saved until you open it in the Editor and hit Save. Prices, payment terms and supplier contact details are never copied across.</p>

      {!busy && !result && (
        <>
          <div
            className={'ai-upload-zone' + (drag ? ' drag' : '')}
            data-testid="ai-upload-zone"
            onClick={() => fileRef.current && fileRef.current.click()}
            onDragOver={e => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={e => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
          >
            <div style={{ fontWeight: 700 }}>Click or drag supplier files here</div>
            <div className="field-hint" style={{ marginTop: 6 }}>PDF · PNG/JPG/WEBP · Word · Excel · PowerPoint — up to 6 files, 30MB total</div>
            <input ref={fileRef} type="file" multiple accept={ACCEPT_ATTR} style={{ display: 'none' }} data-testid="ai-upload-input"
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
          </div>
          {queued.length > 0 && (
            <div className="ai-queue" data-testid="ai-queue">
              {queued.map((f, i) => (
                <span key={f.name + i} className="ai-queue-chip" data-testid={'ai-queued-' + i}>
                  {f.name}
                  <button type="button" data-testid={'ai-queued-remove-' + i} title="Remove"
                    onClick={() => setQueued(q => q.filter((_, idx) => idx !== i))}>✕</button>
                </span>
              ))}
              <button className="btn btn-primary" data-testid="ai-extract-btn" onClick={extract}>
                Build draft from {queued.length} file{queued.length !== 1 ? 's' : ''} →
              </button>
            </div>
          )}
        </>
      )}

      {busy && (
        <div className="ai-busy" data-testid="ai-busy">
          <div className="ai-pulse" data-testid="ai-stage">{stage || 'Reading ' + queued.length + ' file' + (queued.length !== 1 ? 's' : '') + '…'}</div>
          <p className="field-hint">Extracting specs → building the variant table → matching badges → pulling out photos &amp; drawings. Usually 15–40 seconds; a hard document gets a second, more accurate pass. Don't leave this page.</p>
        </div>
      )}

      {result && !busy && (
        <>
          <div className="ai-result-bar">
            <div><b>{result.sheet.name || 'Draft'}</b>{result.sheet.variant ? ' (' + result.sheet.variant + ')' : ''} <span className="field-hint" style={{ margin: 0 }} data-testid="ai-source-line">from {result.filename}{result.model ? ' · read by ' + result.model : ''}</span></div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn" data-testid="ai-suggest-names" onClick={suggestNames} disabled={namesBusy}>{namesBusy ? 'Thinking…' : 'Suggest names'}</button>
              <button className="btn" data-testid="ai-discard" onClick={discard}>Discard</button>
              <button className="btn btn-primary" data-testid="ai-open-editor" onClick={() => onOpenInEditor(result.sheet)}>Open in Editor →</button>
            </div>
          </div>
          {names && names.length > 0 && (
            <div className="ai-names" data-testid="ai-names">
              <b>Name ideas — click to use:</b>
              {names.map(n => (
                <button key={n} className={'ai-name-chip' + (result.sheet.name === n ? ' on' : '')} data-testid={'ai-name-' + n}
                  onClick={() => setResult(r => ({ ...r, sheet: { ...r.sheet, name: n } }))}>{n}</button>
              ))}
              <button className="btn btn-sm" onClick={suggestNames} disabled={namesBusy}>{namesBusy ? '…' : 'More ideas'}</button>
            </div>
          )}
          {result.warnings.length > 0 && (
            <div className="ai-warnings" data-testid="ai-warnings">
              <b>Check these before saving:</b>
              <ul style={{ margin: '6px 0 0 18px' }}>{result.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
          <div className="ai-split">
            <div className="ai-pane">
              <div className="ai-pane-head">Original {pdfUrl ? 'PDF' : 'upload'} {pdfUrl && <a href={pdfUrl} target="_blank" rel="noreferrer" className="field-hint" style={{ margin: 0, fontWeight: 400 }} data-testid="ai-pdf-newtab">open in new tab ↗</a>}</div>
              {pdfUrl
                ? <iframe title="Supplier PDF" className="ai-pdf-frame" data-testid="ai-pdf-frame" src={pdfUrl} />
                : <div className="empty-note" data-testid="ai-no-preview">No PDF to preview — the draft was built from the other uploads.</div>}
            </div>
            <div className="ai-pane">
              <div className="ai-pane-head">AI draft datasheet <span className="field-hint" style={{ margin: 0, fontWeight: 400 }}>review, then Open in Editor</span></div>
              <div className="ai-preview-scroll" data-testid="ai-preview">
                <div className="ai-preview-zoom">
                  <DatasheetPreview sheet={result.sheet} pageId="ai-draft" />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
